<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    header="Person Association"
    :modal="true"
    :closable="true"
    :style="{ width: '100%', height: '100%' }"
  >
    <div v-if="person && scannedData" class="association-container">
      <!-- Comparison Section -->
      <div class="comparison-section mb-4">
        <div class="p-3 border-1 surface-border border-round">
          <div class="flex flex-wrap gap-4 align-items-center">
            <div class="flex align-items-center">
              <span class="text-sm font-medium text-gray-600 mr-1">Name:</span>
              <i
                :class="nameMatch ? 'pi pi-check text-green-600' : 'pi pi-times text-red-600'"
                class="mr-2"
              ></i>
              <span :class="nameMatch ? 'text-green-600' : 'text-red-600'">
                {{ nameMatch ? 'Match' : 'No Match' }}
              </span>
            </div>
            <div class="flex align-items-center">
              <span class="text-sm font-medium text-gray-600 mr-1">ID:</span>
              <i
                :class="idNumberMatch ? 'pi pi-check text-green-600' : 'pi pi-times text-red-600'"
                class="mr-2"
              ></i>
              <span :class="idNumberMatch ? 'text-green-600' : 'text-red-600'">
                {{ idNumberMatch ? 'Match' : 'No Match' }}
              </span>
            </div>
            <div class="flex align-items-center">
              <span class="text-sm font-medium text-gray-600 mr-1">Address:</span>
              <i
                :class="addressMatch ? 'pi pi-check text-green-600' : 'pi pi-times text-red-600'"
                class="mr-2"
              ></i>
              <span :class="addressMatch ? 'text-green-600' : 'text-red-600'">
                {{ addressMatch ? 'Match' : 'No Match' }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Information Grid -->
      <div class="grid mb-4">
        <!-- Person Information -->
        <div class="col-12 lg:col-6">
          <div class="person-section">
            <h3 class="text-lg font-semibold mb-3">Existing Person Information</h3>
            <div class="p-4 border-1 surface-border border-round">
              <div class="grid">
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Name</label>
                    <div class="text-base">{{ person.firstName }} {{ person.lastName }}</div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Email</label>
                    <div class="text-base">{{ person.email || 'Not provided' }}</div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Phone</label>
                    <div class="text-base">{{ person.phone || 'Not provided' }}</div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">ID Number</label>
                    <div class="text-base">{{ person.idNumber || 'Not provided' }}</div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Address</label>
                    <div class="text-base">
                      {{ formatAddress(person) }}
                    </div>
                  </div>
                </div>
                <div class="col-12" v-if="transactionCount > 0">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Transaction History</label>
                    <div class="text-base">
                      <i class="pi pi-shopping-cart mr-2"></i>
                      {{ transactionCount }} transaction{{ transactionCount === 1 ? '' : 's' }} with
                      us
                      <Tag value="Supplier" severity="success" class="ml-2" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Scanned Data Information -->
        <div class="col-12 lg:col-6">
          <div class="scanned-section">
            <h3 class="text-lg font-semibold mb-3">Scanned ID Information</h3>
            <div class="p-4 border-1 surface-border border-round">
              <div class="grid">
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Name</label>
                    <div class="text-base">
                      {{ scannedData.firstName }} {{ scannedData.lastName }}
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">ID Number</label>
                    <div class="text-base">{{ scannedData.idNumber || 'Not provided' }}</div>
                  </div>
                </div>
                <div class="col-12">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Address</label>
                    <div class="text-base">
                      {{ formatScannedAddress(scannedData) }}
                    </div>
                  </div>
                </div>
                <div class="col-12" v-if="scannedData.dateOfBirth">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">Date of Birth</label>
                    <div class="text-base">{{ formatDate(scannedData.dateOfBirth) }}</div>
                  </div>
                </div>
                <div class="col-12" v-if="scannedData.expirationDate">
                  <div class="field">
                    <label class="text-sm font-medium text-gray-600">ID Expiration</label>
                    <div class="text-base">{{ formatDate(scannedData.expirationDate) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="actions-section">
        <div class="flex gap-2 justify-content-end">
          <Button label="Cancel" icon="pi pi-times" class="p-button-text" @click="onCancel" />
          <Button label="Confirm & Merge" icon="pi pi-check" @click="onAssociate" />
        </div>
      </div>
    </div>

    <template #footer>
      <!-- Footer content can be added here if needed -->
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import Tag from 'primevue/tag'
import type { Person } from '@/types/Person'
import type { ScannedIdData } from '@/composables/useIdScanner'
import { usePersonStore } from '@/stores/v2/person'
import { useTransactionService } from '@/services/transactionService'

interface Props {
  visible: boolean
  person: Person | null
  scannedData: ScannedIdData | null
}

interface Emits {
  (e: 'update:visible', value: boolean): void
  (e: 'associate', person: Person): void
  (e: 'update-person', person: Person, scannedData: ScannedIdData): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const personStore = usePersonStore()
const transactionService = useTransactionService()
const transactionCount = ref(0)

// Computed properties for comparison
const nameMatch = computed(() => {
  if (!props.person || !props.scannedData) return false
  return (
    props.person.firstName?.toLowerCase() === props.scannedData.firstName.toLowerCase() &&
    props.person.lastName?.toLowerCase() === props.scannedData.lastName.toLowerCase()
  )
})

const idNumberMatch = computed(() => {
  if (!props.person || !props.scannedData) return false
  return props.person.idNumber === props.scannedData.idNumber
})

const addressMatch = computed(() => {
  if (!props.person || !props.scannedData) return false
  return (
    props.person.streetAddress === props.scannedData.address &&
    props.person.city === props.scannedData.city &&
    props.person.state === props.scannedData.state &&
    props.person.postalCode === props.scannedData.postalCode
  )
})

// Helper functions
function formatAddress(person: Person): string {
  const parts = [person.streetAddress, person.city, person.state, person.postalCode].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : 'Not provided'
}

function formatScannedAddress(data: ScannedIdData): string {
  const parts = [data.address, data.city, data.state, data.postalCode].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : 'Not provided'
}

function formatDate(date: string | Date): string {
  if (!date) return 'Not provided'
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString()
}

// Load transaction count when person changes
watch(
  () => props.person,
  async (newPerson) => {
    if (newPerson?.id) {
      try {
        const transactions = await transactionService.getTransactionsByPersonId(newPerson.id)
        transactionCount.value = transactions.length
      } catch (error) {
        console.error('Failed to load transaction count:', error)
        transactionCount.value = 0
      }
    }
  },
  { immediate: true },
)

// Event handlers
function onAssociate() {
  if (props.person) {
    emit('associate', props.person)
    emit('update:visible', false)
  }
}

function onUpdatePerson() {
  if (props.person && props.scannedData) {
    emit('update-person', props.person, props.scannedData)
    emit('update:visible', false)
  }
}

function onCancel() {
  emit('cancel')
  emit('update:visible', false)
}
</script>

<style scoped>
.association-container {
  height: 100%;
  overflow-y: auto;
}

.field {
  margin-bottom: 1rem;
}

.field:last-child {
  margin-bottom: 0;
}

.person-section,
.scanned-section,
.comparison-section {
  border-left: 4px solid var(--primary-color);
  padding-left: 1rem;
}

.comparison-section {
  border-left-color: var(--orange-500);
}
</style>
